const GOOGLE_MAPS_SCRIPT_URL = 'https://maps.googleapis.com/maps/api/js';
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places';
/** A request that hangs never errors, so it is given up on and treated as failed. */
const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 10000;

let pending: Promise<void> | null = null;

export const isGoogleMapsLoaded = (): boolean =>
  typeof google !== 'undefined' && !!google.maps?.places;

/**
 * Loads the Maps JavaScript API with Places once per page and settles when it is usable.
 *
 * Components that need Places wait on this rather than assuming the script is already there: a
 * route that renders an address field on first paint, such as signup reached from its verification
 * link, otherwise mounts before the script has arrived and react-places-autocomplete throws.
 */
export const loadGoogleMaps = (): Promise<void> => {
  if (isGoogleMapsLoaded()) {
    return Promise.resolve();
  }
  if (!pending) {
    pending = new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (loaded: boolean) => {
        // A timed-out attempt must not settle twice, nor clear a retry that replaced it.
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeout);
        if (loaded) {
          resolve();
          return;
        }
        // A later mount may try again, e.g. once a flaky network is back.
        pending = null;
        reject(new Error('Google Maps failed to load'));
      };
      const timeout = window.setTimeout(() => finish(false), GOOGLE_MAPS_LOAD_TIMEOUT_MS);

      // Removing a script does not cancel its request, so a retry waits on the one still in
      // flight rather than adding a second copy of Maps to the page.
      let script = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = GOOGLE_MAPS_SCRIPT_ID;
        script.src = `${GOOGLE_MAPS_SCRIPT_URL}?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        document.head.appendChild(script);
      }
      const element = script;
      element.addEventListener('load', () => finish(isGoogleMapsLoaded()));
      element.addEventListener('error', () => {
        element.remove();
        finish(false);
      });
    });
  }
  return pending;
};
